import React, { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { CheckCircle, Shield, Award, Headphones } from 'lucide-react'
import Header from './header'
import CustomerHeader from '../customer/CustomerHeader'
import Footer from './Footer'
import Newsletter from './HomeNewsletter'
import { useCustomerAuth } from '../../contexts/CustomerAuthContext'
import './categorypage.css'
import api, { API_BASE_URL } from '../../utils/api';
import Loader from '../common/Loader'

// Types
interface Skill {
  id: number;
  name: string;
  description: string;
}

interface Category {
  id: number;
  name: string;
  description: string;
  icon: string;
  skills?: Skill[];
}

function Categorypage() {
  const { isAuthenticated } = useCustomerAuth();
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchCategories();
  }, []);

  const fetchCategories = async () => {
    try {
      setLoading(true);
      const data = await api.get('/api/customers/categories');

      if (data.success) {
        setCategories(data.categories);
      } else {
        throw new Error(data.message || 'Failed to load categories');
      }
    } catch (err) {
      console.error('Error fetching categories:', err);
      setError(err instanceof Error ? err.message : 'Failed to load categories');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="d-flex flex-column min-vh-100">
        {isAuthenticated ? <CustomerHeader /> : <Header />}
        <div className="flex-grow-1 d-flex align-items-center justify-content-center" style={{ background: '#f8faff' }}>
          <div className="container py-5">
            <div className="text-center">
              <Loader message="Loading categories..." />
            </div>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  if (error) {
    return (
      <div className="d-flex flex-column min-vh-100">
        {isAuthenticated ? <CustomerHeader /> : <Header />}
        <div className="flex-grow-1 d-flex align-items-center justify-content-center" style={{ background: '#f8faff' }}>
          <div className="container py-5">
            <div className="alert alert-danger text-center">
              <h4>Error Loading Categories</h4>
              <p>{error}</p>
              <button
                className="btn btn-primary"
                onClick={() => window.location.reload()}
              >
                Try Again
              </button>
            </div>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="d-flex flex-column min-vh-100">
      {/* Conditionally render header based on authentication */}
      {isAuthenticated ? <CustomerHeader /> : <Header />}

      <div className='category-page flex-grow-1'>
        <div className='container'>
          <div className='category-hero'>
            <h1>Service Categories</h1>
            <p>Find the perfect service provider for your needs</p>
          </div>

          <div className='cat-page-grid'>
            {categories.map((category) => (
              <div key={category.id} className='cat-page-card'>
                <Link to={`/search?category=${category.name}`} className='category-link'>
                  <div className='category-icon'>
                    <img src="/assets/images/cat-one.png" alt={category.name} />
                  </div>
                  <h3>{category.name}</h3>
                  <p className="category-description">{category.description}</p>

                </Link>
              </div>
            ))}
          </div>

          <div className='category-cta'>
            <h2>Can't find what you're looking for?</h2>
            <p>Search our complete directory of service providers</p>
            <Link to='/search' className='btn-primary'>Search All Services</Link>
          </div>
        </div>
      </div>

      <div className='tools-detail'>
        <div className='container'>
          <h4>Featured Services</h4>
          <ul className='feature-list'>
            <li>
              <CheckCircle className="feature-icon" size={20} />
              <span>Verified Professionals</span>
            </li>
            <li>
              <Shield className="feature-icon" size={20} />
              <span>Quality Guaranteed</span>
            </li>
            <li>
              <Award className="feature-icon" size={20} />
              <span>Competitive Pricing</span>
            </li>
            <li>
              <Headphones className="feature-icon" size={20} />
              <span>24/7 Support</span>
            </li>
          </ul>
          <div className='sponse-slider'>
            <div className='row'>
              <div className='col-md-3'>
                <div className='inner-slide-card'>
                  <img
                    className="serice-card-img"
                    src="/assets/images/sc-one.png"
                    alt=""
                  />
                  <h5>Professional Services</h5>
                  <div className='rating'>
                    <img
                      className=""
                      src="/assets/images/rate.png"
                      alt=""
                    /> 4.7 (32 reviews)
                  </div>
                  <p>Responds in about <span>2 days</span></p>
                  <Link to='/search' className='cm-btn'>Discover</Link>
                </div>
              </div>
              <div className='col-md-3'>
                <div className='inner-slide-card'>
                  <img
                    className="serice-card-img"
                    src="/assets/images/sc-one.png"
                    alt=""
                  />
                  <h5>Skilled Professionals</h5>
                  <div className='rating'>
                    <img
                      className=""
                      src="/assets/images/rate.png"
                      alt=""
                    /> 4.8 (45 reviews)
                  </div>
                  <p>Responds in about <span>1 day</span></p>
                  <Link to='/search' className='cm-btn'>Discover</Link>
                </div>
              </div>
              <div className='col-md-3'>
                <div className='inner-slide-card'>
                  <img
                    className="serice-card-img"
                    src="/assets/images/sc-one.png"
                    alt=""
                  />
                  <h5>Expert Services</h5>
                  <div className='rating'>
                    <img
                      className=""
                      src="/assets/images/rate.png"
                      alt=""
                    /> 4.9 (28 reviews)
                  </div>
                  <p>Responds in about <span>3 hours</span></p>
                  <Link to='/search' className='cm-btn'>Discover</Link>
                </div>
              </div>
              <div className='col-md-3'>
                <div className='inner-slide-card'>
                  <img
                    className="serice-card-img"
                    src="/assets/images/sc-one.png"
                    alt=""
                  />
                  <h5>Trusted Providers</h5>
                  <div className='rating'>
                    <img
                      className=""
                      src="/assets/images/rate.png"
                      alt=""
                    /> 4.6 (56 reviews)
                  </div>
                  <p>Responds in about <span>1 day</span></p>
                  <Link to='/search' className='cm-btn'>Discover</Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <Newsletter />
      <Footer />
    </div>
  )
}

export default Categorypage
